"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import useScriptBreakdown from "./use-script-breakdown";
import { Block, BlockType, uuid } from "../types/screenplay-editor.types";
import { ElementCategory, Tag } from "../types/script-breakdown.types";

export interface UseScriptBreakdownSceneOptions {
  scene_url: string;
  fetchOptions?: RequestInit;
  onAISummarize?: (scene: any) => void;
  onTagAdded?: (tag: Tag) => void;
  onTagsBulkAdded?: (tags: Tag[], summary?: string) => Promise<void>;
  onTagRemoved?: (tagId: string) => void;
  onTagUpdated?: (
    tagId: string,
    categoryId: ElementCategory | null,
    details?: Partial<Tag>,
  ) => void;
  preLoadedTags?: Tag[];
  preLoadedTagsLoading?: boolean;
}

export function useScriptBreakdownScene(
  options: UseScriptBreakdownSceneOptions,
) {
  // const { scenes, isLoading, error } = useScriptBreakdown({ scenes: [] });
  const [tags, setTags] = useState<Tag[]>(options.preLoadedTags || []);
  const [selectionMenu, setSelectionMenu] = useState<{
    blockId: string;
    startIndex: number;
    endIndex: number;
    text: string;
    top: number;
    left: number;
  } | null>(null);
  const autoTaggedSceneRef = useRef<string | null>(null);
  const [scene, setScene] = useState<any>(null);
  const [menuPlacement, setMenuPlacement] = useState<"top" | "bottom">("top");
  const [sceneBrief, setSceneBrief] = useState<string>("");
  const [isSummarizing, setIsSummarizing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoading(true);
    const fetchScene = async () => {
      try {
        const response = await fetch(options.scene_url, options.fetchOptions);
        if (response.ok) {
          const text = await response.text();
          setScene({ content: text });
        } else {
          console.error("Failed to fetch scene:", response);
          setError(true);
        }
        setIsLoading(false);
      } catch (error) {
        setError(true);
        setIsLoading(false);
        console.error("Error fetching scene:", error);
      }
    };
    fetchScene();
  }, []);

  const blocks = useMemo(() => {
    if (!scene || !scene.content) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(scene.content, "text/html");
    const divs = Array.from(doc.querySelectorAll("div"));
    const parsedBlocks: Block[] = [];

    const typeMap: Record<string, BlockType> = {
      divtype0: "SCENE_HEADING",
      divtype2: "ACTION",
      divtype3: "CHARACTER",
      divtype4: "PARENTHETICAL",
      divtype5: "DIALOGUE",
      divtype6: "TRANSITION",
    };

    divs.forEach((div) => {
      const divText = div.textContent?.trim() || "";
      if (!divText) return;

      let type: BlockType = "ACTION";
      for (const className of Array.from(div.classList)) {
        if (typeMap[className]) {
          type = typeMap[className];
          break;
        }
      }

      const idAttr = div.getAttribute("id");
      const blockId =
        idAttr && idAttr.startsWith("par")
          ? idAttr.substring(3)
          : idAttr || uuid();

      parsedBlocks.push({ id: blockId, type, text: divText });
    });

    return parsedBlocks;
  }, [scene]);

  const characters = useMemo(() => {
    const chars = blocks
      .filter((b) => b.type === "CHARACTER")
      .map((b) => {
        const text = b.text.trim().toUpperCase();
        const parenIndex = text.indexOf("(");
        return parenIndex > -1 ? text.substring(0, parenIndex).trim() : text;
      })
      .filter(Boolean);

    return [...new Set(chars)];
  }, [blocks]);

  const handleAISummarize = async () => {
    if (!scene || !scene.content) {
      console.error("Cannot summarize scene: Scene data is not loaded yet.");
      return;
    }

    setIsSummarizing(true);
    try {
      const res: any = await options.onAISummarize?.(scene.content);

      if (res && res.ok) {
        const data = await res.json();

        let parsedData: any[] = [];
        let parsedSummaryData: any = {};

        try {
          const normalData = JSON.parse(data.data);
          if (Array.isArray(normalData)) {
            parsedData = Array.isArray(normalData[0]) ? normalData[0] : [];
            const summary = Array.isArray(normalData[1]) ? normalData[1] : [];
            parsedSummaryData = summary[0] || {};
          }
        } catch (error) {
          console.error("Error parsing AI summary data:", error);
        }

        setSceneBrief(parsedSummaryData.summarise || "");

        const newTags: Tag[] = [];

        parsedData.forEach((aiTag: any) => {
          if (
            !aiTag.block_id ||
            !aiTag.category_id ||
            typeof aiTag.start_index !== "number" ||
            typeof aiTag.end_index !== "number"
          ) {
            return;
          }

          const newTag: Tag = {
            id: aiTag.id || uuid(),
            block_id: String(aiTag.block_id).startsWith("par")
              ? String(aiTag.block_id).substring(3)
              : String(aiTag.block_id),
            category_id: aiTag.category_id,
            name: aiTag.name,
            start_index: aiTag.start_index,
            end_index: aiTag.end_index,
          };

          if (
            aiTag.category_id === "PROP" ||
            aiTag.category_id === "SET_PROP" ||
            aiTag.category_id === "VEHICLE"
          ) {
            newTag.quantity = 1;
          } else if (aiTag.category_id === "CAST") {
            newTag.look = "";
            newTag.age = "";
          }

          newTags.push(newTag);
        });

        if (newTags.length > 0) {
          const originalTags = tags;
          setTags((prev) => {
            const merged = [...prev];
            newTags.forEach((newTag) => {
              // Prevent overlapping tags
              const isOverlapping = merged.some(
                (t) =>
                  t.block_id === newTag.block_id &&
                  newTag.end_index > t.start_index &&
                  newTag.start_index < t.end_index,
              );
              if (!isOverlapping) {
                merged.push(newTag);
              }
            });
            return merged;
          });

          try {
            if (options.onTagsBulkAdded) {
              await options.onTagsBulkAdded(newTags, parsedSummaryData.summarise);
            }
          } catch (error) {
            console.error("Failed to bulk add AI-generated tags:", error);
            setTags(originalTags);
          }
        }

        return data;
      } else {
        console.error("Failed to summarize scene:", res);
      }
    } catch (error) {
      console.error("An error occurred during scene summarization:", error);
    } finally {
      setIsSummarizing(false);
    }
  };

  const bulkCreateTags = useCallback(async () => {
    if (blocks.length === 0) return;

    const newTags: Tag[] = [];
    const seenCharacters = new Set<string>();
    const timeOfDays = ["DAY", "NIGHT"];
    const isTimeOfDay = (str: string) => timeOfDays.includes(str.toUpperCase());

    blocks.forEach((block) => {
      if (block.type === "CHARACTER") {
        const text = block.text.trim();
        const parenIndex = text.indexOf("(");
        const charName =
          parenIndex > -1 ? text.substring(0, parenIndex).trim() : text;

        if (charName && !seenCharacters.has(charName.toUpperCase())) {
          seenCharacters.add(charName.toUpperCase());
          const startIndex = text.indexOf(charName);
          if (startIndex !== -1) {
            newTags.push({
              id: uuid(),
              block_id: block.id,
              category_id: "CAST" as unknown as ElementCategory,
              name: charName,
              start_index: startIndex,
              end_index: startIndex + charName.length,
              look: "",
              age: "",
            });
          }
        }
      } else if (block.type === "SCENE_HEADING") {
        const text = block.text.trim();
        const typeMatch = text.match(
          /^(INT\/EXT|INT\.?\/EXT\.?|INT\.EXT\.?|INT|EXT|I\/E)\.?\s+/i,
        );
        let remainingText = text;
        let offset = 0;

        if (typeMatch) {
          offset = typeMatch[0].length;
          remainingText = text.substring(offset);
        }

        const parts = remainingText.split(/\s+-\s+/);
        if (parts.length > 0) {
          const locationName = parts[0].trim();
          const locStart = text.indexOf(locationName, offset);
          if (locStart !== -1 && locationName) {
            newTags.push({
              id: uuid(),
              block_id: block.id,
              category_id: "LOCATION" as unknown as ElementCategory,
              name: locationName,
              start_index: locStart,
              end_index: locStart + locationName.length,
            });
          }

          if (parts.length > 1) {
            const secondPart = parts[1].trim();
            const isLast = parts.length === 2;
            // Only treat the second part as sublocation if it's not the time of day
            if (!isLast || !isTimeOfDay(secondPart)) {
              const subLocStart = text.indexOf(
                secondPart,
                locStart + locationName.length,
              );
              if (subLocStart !== -1 && secondPart) {
                newTags.push({
                  id: uuid(),
                  block_id: block.id,
                  category_id: "SUBLOCATION" as unknown as ElementCategory,
                  name: secondPart,
                  start_index: subLocStart,
                  end_index: subLocStart + secondPart.length,
                });
              }
            }
          }
        }
      }
    });

    if (newTags.length > 0) {
      const originalTags = tags;
      setTags((prev) => {
        const merged = [...prev];
        // Ensure characters are only highlighted once across both existing and new tags
        const existingChars = new Set(
          merged
            .filter(
              (t) =>
                t.category_id === ("CHARACTER" as unknown as ElementCategory) ||
                t.category_id === ("CAST" as unknown as ElementCategory),
            )
            .map((t) => t.name.toUpperCase()),
        );

        newTags.forEach((newTag) => {
          if (
            newTag.category_id === ("CHARACTER" as unknown as ElementCategory)
          ) {
            if (existingChars.has(newTag.name.toUpperCase())) return;
            existingChars.add(newTag.name.toUpperCase());
          }

          const isOverlapping = merged.some(
            (t) =>
              t.block_id === newTag.block_id &&
              newTag.end_index > t.start_index &&
              newTag.start_index < t.end_index,
          );
          if (!isOverlapping) {
            merged.push(newTag);
          }
        });
        return merged;
      });

      try {
        if (options.onTagsBulkAdded) {
          await options.onTagsBulkAdded(newTags);
        }
      } catch (error) {
        console.error("Failed to bulk create tags:", error);
        setTags(originalTags);
      }
    }
  }, [blocks, tags, options.onTagsBulkAdded]);

  // Reset tags when scene changes to prevent stale tags between navigation
  useEffect(() => {
    setSceneBrief("");
    autoTaggedSceneRef.current = null;
  }, [options.scene_url]);

  useEffect(() => {
    if (options.preLoadedTags && options.preLoadedTags.length > 0) {
      setTags((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(options.preLoadedTags)) {
          return prev;
        }
        return options.preLoadedTags || [];
      });
    }
  }, [options.preLoadedTags]);

  useEffect(() => {
    const doBulkCreate = async () => {
      if (blocks.length === 0) return;

      if (options.preLoadedTagsLoading) return;

      if (autoTaggedSceneRef.current === options.scene_url) return;

      const hasPreloadedTags =
        options.preLoadedTags && options.preLoadedTags.length > 0;

      autoTaggedSceneRef.current = options.scene_url;

      if (hasPreloadedTags) return;

      await bulkCreateTags();
    };

    doBulkCreate();
  }, [
    blocks,
    options.scene_url,
    options.preLoadedTags,
    options.preLoadedTagsLoading,
  ]);

  const clearSelection = useCallback(() => {
    setSelectionMenu(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  // Reset placement to default when selection closes
  useEffect(() => {
    if (!selectionMenu) {
      setMenuPlacement("top");
    }
  }, [selectionMenu]);

  // Flip menu if it goes off screen
  useEffect(() => {
    if (selectionMenu && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      if (menuPlacement === "top" && rect.top < 100) {
        setMenuPlacement("bottom");
      } else if (
        menuPlacement === "bottom" &&
        rect.bottom > window.innerHeight - 40 &&
        rect.top > 300
      ) {
        setMenuPlacement("top");
      }
    }
  }, [selectionMenu, menuPlacement]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectionMenu && !(e.target as HTMLElement).closest(".tag-menu")) {
        clearSelection();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectionMenu, clearSelection]);

  const getAbsoluteOffset = (
    container: HTMLElement,
    targetNode: Node,
    targetOffset: number,
  ) => {
    let absoluteOffset = 0;
    let found = false;

    const traverse = (node: Node) => {
      if (found) return;
      if (node === targetNode) {
        absoluteOffset += targetOffset;
        found = true;
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        absoluteOffset += node.nodeValue?.length || 0;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          traverse(node.childNodes[i]);
          if (found) return;
        }
      }
    };

    traverse(container);
    return found ? absoluteOffset : null;
  };

  const handleMouseUp = () => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.toString().trim())
      return;

    const range = selection.getRangeAt(0);
    let container = range.commonAncestorContainer as HTMLElement;
    if (container.nodeType === Node.TEXT_NODE)
      container = container.parentElement!;

    const blockElem = container.closest("[data-block-id]") as HTMLElement;
    if (!blockElem) return;

    const blockId = blockElem.getAttribute("data-block-id")!;
    const startOffset = getAbsoluteOffset(
      blockElem,
      range.startContainer,
      range.startOffset,
    );
    const endOffset = getAbsoluteOffset(
      blockElem,
      range.endContainer,
      range.endOffset,
    );

    if (startOffset !== null && endOffset !== null) {
      const rect = range.getBoundingClientRect();
      const blockRect = blockElem.getBoundingClientRect();
      setSelectionMenu({
        blockId,
        startIndex: Math.min(startOffset, endOffset),
        endIndex: Math.max(startOffset, endOffset),
        text: selection.toString().trim(),
        top: rect.top - blockRect.top,
        left: rect.left - blockRect.left + rect.width / 2,
      });
    }
  };

  const addTag = async (categoryId: ElementCategory) => {
    if (!selectionMenu) return;
    const newTag: Tag = {
      id: uuid(),
      block_id: selectionMenu.blockId,
      category_id: categoryId,
      name: selectionMenu.text,
      start_index: selectionMenu.startIndex,
      end_index: selectionMenu.endIndex,
    };

    if (
      categoryId === "PROP" ||
      categoryId === "SET_PROP" ||
      categoryId === "VEHICLE"
    ) {
      newTag.quantity = 1;
    } else if (categoryId === "CAST") {
      newTag.look = "";
      newTag.age = "";
    }

    // Optimistically update the UI
    setTags((prev) => {
      const filtered = prev.filter(
        (t) =>
          t.block_id !== newTag.block_id ||
          !(
            newTag.end_index > t.start_index && newTag.start_index < t.end_index
          ),
      );
      return [...filtered, newTag];
    });
    clearSelection();

    try {
      await options.onTagAdded?.(newTag);
    } catch (error) {
      console.error("Failed to add tag:", error);
      // On failure, revert the optimistic update
      setTags((prev) => prev.filter((t) => t.id !== newTag.id));
    }
  };

  const removeTag = async (e: React.MouseEvent, id?: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (!id) return;
    const tagToRemove = tags.find((t) => t.id === id);
    if (!tagToRemove) return;

    // Optimistically remove the tag from the UI
    setTags((prev) => prev.filter((t) => t.id !== id));
    clearSelection();

    try {
      await options.onTagRemoved?.(id);
    } catch (error) {
      console.error("Failed to remove tag:", error);
      // On failure, revert the optimistic update by adding the tag back
      setTags((prev) => [...prev, tagToRemove]);
    }
  };

  const updateTag = async (
    id: string,
    categoryId?: ElementCategory | null,
    details?: Partial<Tag>,
  ) => {
    const tagToUpdate = tags.find((t) => t.id === id);
    if (!tagToUpdate) return;

    const updatedFields: Partial<Tag> = { ...details };
    if (categoryId) {
      updatedFields.category_id = categoryId;
      if (
        categoryId === "PROP" ||
        categoryId === "SET_PROP" ||
        categoryId === "VEHICLE"
      ) {
        updatedFields.quantity = tagToUpdate.quantity ?? 1;
      } else if (categoryId === "CAST") {
        updatedFields.look = tagToUpdate.look ?? "";
        updatedFields.age = tagToUpdate.age ?? "";
      }
    }

    // Optimistically update the UI
    setTags((prev) =>
      prev.map((t) => {
        if (t.id === id) return { ...t, ...updatedFields };
        // Sync details across identically named tags in the same category
        if (
          details &&
          t.name === tagToUpdate.name &&
          t.category_id === tagToUpdate.category_id
        ) {
          return { ...t, ...details };
        }
        return t;
      }),
    );
    if (categoryId) clearSelection();

    try {
      await options.onTagUpdated?.(id, categoryId || null, details);
    } catch (error) {
      console.error("Failed to update tag:", error);
      // On failure, revert the optimistic update
      setTags((prev) => prev.map((t) => (t.id === id ? tagToUpdate : t)));
    }
  };

  return {
    scene,
    blocks,
    characters,
    isLoading,
    error,
    tags,
    selectionMenu,
    handleMouseUp,
    addTag,
    updateTag,
    removeTag,
    clearSelection,
    menuPlacement,
    menuRef,
    sceneBrief,
    setSceneBrief,
    handleAISummarize,
    isSummarizing,
  };
}
